<template>
  <ListNavigation
    class="selection-list"
    :class="selectionListClasses"
    :options="options"
    :key-prop="keyProp"
    element-selector=".selection-list__row"
    :first-element-sticky="firstElementSticky"
    :last-element-sticky="lastElementSticky"
    :grid-gap="density === 'dense' ? 4 : 16"
    :track-options-to-render="trackOptionsToRender"
    @focus-current="(key) => focusIfNeeded(key)"
    ref="ListNavigation"
    v-slot="{ currentKey, focusKey, highlightKey, optionsToRender }"
  >
    <div
      class="selection-list__body"
      :style="{ 'max-height': !horizontal && maxSize, 'max-width': horizontal && maxSize }"
      :class="{
        [`selection-list__body--${density}`]: true,
        'selection-list__body--focus-margin': focusMargin,
      }"
    >
      <template v-for="(option, index) in options" :key="option[keyProp]">
        <div
          v-if="
            hasGroups &&
            option.group &&
            ((options[index - 1] && options[index - 1].group !== option.group) || !options[index - 1])
          "
          :data-test-selection-list-group="option.group"
          :data-list-group-sticky-first="firstElementSticky && index === 0"
          :data-list-group-sticky-last="lastElementSticky && index === options.length - 1"
          :class="[
            'selection-list__group',
            {
              'selection-list__group--sticky-first': firstElementSticky && index === 0,
              'selection-list__group--sticky-last': lastElementSticky && index === options.length - 1,
            },
          ]"
          :key="`group-start-${option[keyProp]}`"
        >
          <CheckBoxToggle
            v-if="multi && groups[option.group].toggle"
            position="end"
            @change="toggleGroup(option.group, option.value)"
            :checked="groupSelectionStatus[option.group]"
            :indeterminate="groupSelectionStatus[option.group] === null"
            :id="`${listId}-group-toggle-${option.group}`"
          >
            <span :title="groups[option.group].name" v-if="groups[option.group].name" class="selection-list__label">{{
              groups[option.group].name
            }}</span>
          </CheckBoxToggle>
          <span
            v-else-if="groups[option.group].name"
            :title="groups[option.group].name"
            class="selection-list__label"
            >{{ groups[option.group].name }}</span
          >
        </div>
        <div
          class="selection-list__row"
          :class="{
            current: option[keyProp] === currentKey,
            highlighted: option[keyProp] === highlightKey,
            selected: selectionMap.has(option.value),
            clickable: Boolean(clickableOption),
            'selection-list__row--sticky-first': firstElementSticky && index === 0,
            'selection-list__row--sticky-last': lastElementSticky && index === options.length - 1,
            'selection-list__row--last-group-item':
              hasGroups && index < options.length - 1 && option.group !== options[index + 1].group,
          }"
          :data-list-item-key="option[keyProp]"
          :data-list-item-sticky-first="firstElementSticky && index === 0"
          :data-list-item-sticky-last="lastElementSticky && index === options.length - 1"
          @focusin="() => setPosition(index)"
        >
          <div
            class="selection-list__custom-control"
            data-test-id="selection-list-custom-control"
            v-if="clickableOption === 'custom-control'"
          >
            <slot
              v-bind="{
                value: option.value,
                label: option.label,
                isSelected: selectionMap.has(option.value),
                group: option.group,
                isFocussed: option[keyProp] === focusKey,
                isHighlighted: option[keyProp] === highlightKey,
                disabled: option.disabled,
                index,
                shouldRender: trackOptionsToRender ? optionsToRender.has(option[keyProp]) : true,
                toggleSelection: ($event) => toggleSelected($event, option.value, index),
              }"
            >
              {{ option.label }}
            </slot>
          </div>
          <div v-else class="selection-list__input">
            <div class="selection-list__input--content">
              <component
                class="selection-list__input--element"
                :is="multiStyle"
                v-if="multi"
                :checked="selectionMap.has(option.value)"
                :disabled="option.disabled"
                :id="`${listId}-${option[keyProp]}`"
                :position="controlPosition"
                @keyup.escape="handleEscape"
                @keyup.enter.exact="($event) => (option.disabled ? null : toggleSelected($event, option.value, index))"
                @change="($event) => (option.disabled ? null : toggleSelected($event, option.value, index))"
                data-test-id="selection-list-check-box"
              >
                <slot
                  v-if="clickableOption"
                  v-bind="{
                    value: option.value,
                    label: option.label,
                    isSelected: selectionMap.has(option.value),
                    group: option.group,
                  }"
                >
                  {{ option.label }}
                </slot>
              </component>
              <RadioButton
                v-else
                @keyup.enter.exact="toggleSelected($event, option.value, index)"
                @click="($event) => (option.disabled ? null : toggleSelected($event, option.value, index))"
                class="selection-list__input--element"
                :model="selectionMap.has(option.value)"
                :modelValue="true"
                :disabled="option.disabled"
                :name="option.value"
                :icon="option.icon"
                :position="controlPosition"
              >
                <slot
                  v-if="clickableOption"
                  v-bind="{
                    value: option.value,
                    label: option.label,
                    isSelected: selectionMap.has(option.value),
                    group: option.group,
                  }"
                >
                  {{ option.label }}
                </slot>
              </RadioButton>
            </div>
          </div>

          <!-- if clickableOption=false, render the slot after radio / multi, so that is in fact, not clickable -->
          <slot
            v-if="!clickableOption"
            v-bind="{
              value: option.value,
              label: option.label,
              isSelected: selectionMap.has(option.value),
              group: option.group,
            }"
          >
            {{ option.label }}
          </slot>
        </div>
      </template>
      <div v-if="options.length === 0" key="emptystate">
        <div class="selection-list__empty" v-html="emptyText" />
      </div>
    </div>
  </ListNavigation>
</template>

<script lang="ts">
/* eslint-disable max-lines */
import { defineComponent, PropType } from 'vue';

import { symmetricDifference, union, pluck, difference } from 'ramda';
import getMultiSelectedItems from '@utils/getMultiSelectedItems';
import ListNavigation from '@components/ListNavigation.vue';

import { v4 as uuid } from 'uuid';

export type ListItem<Context = string> = {
  label: Context;
  value: any;
  disabled?: boolean;
  group?: string;
  [key: string]: any;
};

export type ListOptions<Context = any> = Array<ListItem<Context>>;

export type ListItemGroup = {
  name: string;
  icon?: string;
  /**
   * Display a toggle for the group that toggles all items in it
   */
  toggle?: boolean;
};

// The key must match the group property of an option
export type ListGroups = Record<string, ListItemGroup>;

export const nothingSelected = Symbol('nothingSelected');

export default defineComponent({
  name: 'SelectionList',

  components: {
    ListNavigation,
  },

  props: {
    options: {
      type: Array as PropType<ListOptions>,
      required: true,
    },

    /**
     * Unique property on the option, used for various purposes, such as v-for keys,
     * and position tracking.
     *
     * The default "value" assumes that value is a primitive
     */
    keyProp: {
      type: String,
      required: false,
      default: 'value',
    },

    /**
     * Value(s) should be primitve types.
     * (Objects would work if the object in the value and the options is identical, because
     * we use a map to track selection)
     */
    modelValue: {
      required: true,
      type: [String, Number, Array, Symbol, Boolean],
    },

    /**
     * Object of possible groups
     * The key must match the group property of an option
     */
    groups: {
      type: Object as PropType<ListGroups>,
      required: false,
      default: () => ({}),
    },

    multi: {
      type: Boolean,
      default: false,
    },

    /**
     * Style of the multi-select input.
     * Default is CheckBox to left.
     * CheckBoxToggle is rendered to the right.
     */
    multiStyle: {
      type: String as PropType<'CheckBox' | 'CheckBoxToggle'>,
      required: false,
      default: 'CheckBox',
    },

    /**
     * Where the control (checkbox etc) is positioned (relative to the content) for built-in controls
     */
    controlPosition: {
      type: String as PropType<'start' | 'end'>,
      default: 'end',
    },

    emptyText: {
      type: String,
      default: '',
    },

    showOnHover: {
      type: Boolean,
      default: false,
    },

    /**
     * css max-height (or max-width if horizontal is true)
     */
    maxSize: {
      type: String,
      default: null,
    },

    // the density is getting out of hand, we will look at it when we look at the whole thing
    // i just need a way to match the new design for now
    // also the passing of the correct gap to the list navigation is messed up
    density: {
      type: String as PropType<'normal' | 'dense' | 'medium' | '8'>,
      default: 'normal',
    },

    /**
     * How the option can be interacted with.
     * If true, the control and the option can be clicked
     * If false, only the control can be clicked
     * If 'custom-control', only the option is rendered, and the consumer component has to use the
     * toggleSelection() method of the slot to trigger a select on click and on enter keypress.
     *
     * Also, isSelected for selection and isHighlighted for current position need to have CSS.
     */
    clickableOption: {
      type: [Boolean, String] as unknown as PropType<boolean | 'custom-control'>,
      default: false,
    },

    alwaysEmit: {
      type: Boolean,
      default: false,
    },

    horizontal: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to make the first element  (and its group marker sticky)
     * Used by CodeSelect and only tested with it
     */
    firstElementSticky: {
      type: Boolean,
      default: false,
    },

    /**
     * Whether to add a negative margin to selection-list-body to show focus correctly
     */
    focusMargin: {
      type: Boolean,
      default: true,
    },

    /**
     * Whether to make the last element  (and its group marker sticky)
     * Previously used by CodeSelect and only tested with it.
     * TODO:
     * Currently covers the scrollbar (probably needs to be replaced by a real element)
     */
    lastElementSticky: {
      type: Boolean,
      default: false,
    },

    /**
     * If it is allowed to unselect a selected value when multi is false.
     * i.e. the user can unselect if a default value exists or once a selection is made
     */
    allowEmptySingleSelect: {
      type: Boolean,
      default: false,
    },

    /**
     * If the ListNavigation component should track which elemnts are currently visible, and
     * which ones are almost visible.
     * This info can be used by custom SelectionList components (clickableOption=custom-control) to conditionally render
     * themselves
     */
    trackOptionsToRender: {
      type: Boolean,
      default: false,
    },
  },
  emits: { 'update:modelValue': null, cancel: null },

  data(): {
    listId: string;
  } {
    return {
      listId: uuid(),
    };
  },

  computed: {
    selectionListClasses(): Record<string, boolean> {
      return {
        'selection-list--has-selection': this.hasSelection,
        'selection-list--show-on-hover': this.showOnHover,
        'selection-list--horizontal': this.horizontal,
        'selection-list--clickable-option': Boolean(this.clickableOption),
        'selection-list--custom-control': this.clickableOption === 'custom-control',
        'selection-list--first-element-sticky': this.firstElementSticky && this.options.length > 0,
        'selection-list--last-element-sticky': this.lastElementSticky && this.options.length > 1,
        'selection-list--first-element-sticky-grouped':
          this.firstElementSticky && this.options.length > 0 && this.options[0].group && this.hasGroups,
        'selection-list--last-element-sticky-grouped':
          this.lastElementSticky &&
          this.options.length > 1 &&
          this.options[this.options.length - 1].group &&
          this.hasGroups,
      };
    },

    // Map of all selected values.
    // Makes it possible to easily find out if an option is selected,
    // because map keys can be of any type
    selectionMap(): Map<any, true> {
      const map = new Map();

      if (this.multi) {
        (this.modelValue as []).forEach((val) => {
          map.set(val, true);
        });
      } else if (this.modelValue !== null && this.modelValue !== undefined) {
        map.set(this.modelValue, true);
      }

      return map;
    },

    hasSelection(): boolean {
      return this.selectionMap.size > 0;
    },

    hasGroups(): boolean {
      return Object.keys(this.groups).length > 0;
    },

    groupItemCounts(): Map<string, number> {
      return this.options.reduce((acc, option) => {
        if (option.group) {
          const count = acc.get(option.group);

          if (count === undefined) {
            acc.set(option.group, 1);
          } else {
            acc.set(option.group, count + 1);
          }
        }

        return acc;
      }, new Map<string, number>());
    },

    /**
     * Compute if groups are selected / indeterminate
     */
    groupSelectionStatus(): Record<string, boolean | null> {
      const selectedOptions = this.options.filter((option) => this.selectionMap.has(option.value));

      const groupItemCounts = selectedOptions.reduce((acc, option) => {
        if (option.group) {
          const count = acc.get(option.group);

          if (count === undefined) {
            acc.set(option.group, 1);
          } else {
            acc.set(option.group, count + 1);
          }
        }

        return acc;
      }, new Map<string, number>());

      const groupSelectionStatus: Record<string, boolean | null> = {};

      groupItemCounts.forEach((count, id) => {
        const groupItemCount = this.groupItemCounts.get(id);
        let result = groupItemCount === count;

        if (!result && count > 0) {
          result = /* indeterminate */ null;
        }

        groupSelectionStatus[id] = result;
      });

      return groupSelectionStatus;
    },
  },

  methods: {
    handleEscape() {
      this.$emit('cancel');
    },

    toggleSingleSelected(value) {
      if (this.multi) {
        this.selectionChange(symmetricDifference(this.modelValue, [value]));
      } else if (value === this.modelValue && this.allowEmptySingleSelect) {
        this.selectionChange(nothingSelected);
        // eslint-disable-next-line eqeqeq
      } else if (value != this.modelValue || this.alwaysEmit) {
        this.selectionChange(value);
      }
    },

    toggleGroup(groupId: string, firstGroupMemberKey: string) {
      const groupMembers = this.options.flatMap((option) => (option.group === groupId ? [option[this.keyProp]] : []));

      if (this.modelValue.includes(firstGroupMemberKey)) {
        const unselected = difference(groupMembers, this.modelValue);

        if (unselected.length) {
          // select all
          this.selectionChange([...unselected, ...this.modelValue]);
        } else {
          // deselect all
          this.selectionChange(this.modelValue.filter((val) => !groupMembers.includes(val)));
        }
      } else {
        // select all (optimized)
        this.selectionChange(union(groupMembers, this.modelValue));
      }
    },

    toggleMultiSelected(value: Array<string>) {
      this.selectionChange(
        union(this.modelValue, getMultiSelectedItems(pluck('value', this.options))(this.modelValue)(value))
      );
    },

    toggleSelected(event: Event, value: any, index: number) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).setPosition(index, {
        focus: true,
        highlight: true,
      });

      if (event.shiftKey && this.multi) {
        this.toggleMultiSelected(value);
      } else {
        this.toggleSingleSelected(value);
      }
    },

    selectionChange(selection) {
      this.$emit('update:modelValue', selection);
    },
    /**
     * @public
     */
    next(...args) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).next(...args);
    },
    /**
     * @public
     */
    previous(...args) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).previous(...args);
    },

    setPosition(index: number) {
      // component might be unmounted
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>)?.setPosition(index, { highlight: true });
    },
    /**
     * @public
     */
    startHighlighting(...args) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).startHighlighting(...args);
    },
    /**
     * @public
     */
    stopHighlighting(...args) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>)?.stopHighlighting(...args);
    },
    /**
     * @public
     */
    resetFocusState(...args) {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>)?.resetFocusState(...args);
    },
    /**
     * @public
     */
    resetPositionData() {
      (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).resetPositionData();
    },
    /**
     * @public
     */
    selectCurrent() {
      const currentOption = (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).getCurrent();

      if (currentOption && !currentOption.disabled) {
        this.toggleSingleSelected(currentOption.value);

        return currentOption.value;
      }

      return nothingSelected;
    },

    /**
     * Focusses the input in the active row if the SelectionList uses the default radio / checkbox
     */
    focusIfNeeded(key: string) {
      if (this.clickableOption !== 'custom-control') {
        (this.$el as Element).querySelector(`[data-list-item-key="${key}"] input`).focus();
      }
    },

    /**
     * @public
     */
    getCurrentOption() {
      return (this.$refs.ListNavigation as InstanceType<typeof ListNavigation>).getCurrent();
    },
  },
});
</script>

<style lang="scss">
@use 'settings/colors';
@use 'mixins/typography';
@use 'placeholders/label';

.selection-list {
  color: colors.$color-grey-900;
}

.selection-list--big {
  --input-gap: 16px;
}

.selection-list__body {
  /**
   * The gap between the list items
   */
  --input-gap: 16px;
  /**
   * Extra gap at the end of a group
   */
  --group-end-gap: 14px;
  /**
   * Extra gap for the next item after a sticky item to prevent overlapping the next item's focus outline
   */
  --sticky-first-extra-gap: 0;

  --focus-offset: 4px;
  padding: var(--focus-offset);

  display: grid;
  position: relative;
  overflow-y: auto;
  overflow-x: hidden;

  &--dense {
    --input-gap: 0;
    --sticky-first-extra-gap: 24px;
  }

  // Tim had to add this for the select quotations dialog. --dense was 4 before, but just needed 4 right now
  // TODO: consolidate
  &--medium {
    --input-gap: 4px;
    --sticky-first-extra-gap: 16px;
    grid-gap: 4px;
  }

  &--8 {
    --input-gap: 8px;
    --sticky-first-extra-gap: 16px;
    grid-gap: 8px;
  }

  & {
    grid-gap: var(--input-gap);
  }

  > div {
    min-width: 0; // prevent text overflow
  }

  &--focus-margin {
    // Adjustments for focus ring / box-shadow used by CodeSelect
    // Without this, the box-shadow is clipped by the overflow behavior
    margin: calc(var(--focus-offset) * -1);
  }
}

.selection-list__row {
  border-radius: 8px;
  min-height: 32px;
  display: flex;
  align-items: center;

  --background: #{colors.$color-white};
  &.clickable {
    cursor: pointer;
  }
  &.highlighted {
    z-index: 50;
    background-color: colors.$color-grey-100;
  }

  &.selection-list__row--last-group-item {
    + .selection-list__group {
      margin-top: var(--group-end-gap);
    }

    + .selection-list__row {
      margin-top: var(--group-end-gap);
    }
  }
}

.selection-list--custom-control {
  &.highlighted {
    background-color: inherit;
  }
}

.selection-list.selection-list--clickable-option {
  .selection-list__row {
    &:hover {
      background-color: colors.$color-grey-100;
    }

    &.current:focus-within {
      box-shadow: 0px 0px 0px 2px colors.$color-blue-100;
    }
  }
}

.selection-list__input {
  width: 100%;
  min-height: 32px;
  display: flex;
  align-items: center;
}

.selection-list__input--content {
  width: 100%;
}

.selection-list__input--element {
  padding: 0 6px 0 8px;
  min-height: 32px;
}

.selection-list--horizontal .selection-list__body {
  grid-auto-flow: column;
  justify-content: start;

  overflow-x: auto;
  overflow-y: hidden;
}

.selection-list--custom-control {
  .selection-list__row {
    display: flex;
    justify-content: center;
    flex-direction: column;
  }

  .selection-list__custom-control {
    width: 100%;
  }
}

.selection-list--show-on-hover {
  margin-left: -36px; // move check boxes out of container

  .selection-list__input {
    opacity: 0.75;
  }

  .selection-list__row:hover {
    .selection-list__input {
      opacity: 1;
    }
  }

  &.selection-list--has-selection {
    .selection-list__input {
      opacity: 1;
    }
  }
}

.selection-list__group {
  padding-left: 4px;
  padding-right: 4px;
  height: 26px;
  display: flex;
  align-items: center;

  // groups without name
  &:empty {
    height: 0;
  }
}

.selection-list__label {
  @extend %generic-label;

  @include typography.typography-ellipsis;
  display: inline-block;
}

.selection-list__empty {
  padding: 4px;
}

/** Styles for sticky first and last element
 *
 * Sticky only works well with the SelectionList for the project codes in the CodeSelect
 *
 **/

/**
 * These two pseudo elements cover the background of the sticky elements / list
 * that is not covered by the elements itself (see below),
 * and add the separator border (box-shadow)
 */
.selection-list--first-element-sticky {
  &::before {
    content: '';
    background: colors.$color-grey-50;
    position: absolute;
    width: 100%;
    height: 43px;
    top: 0;
    left: 0;
    right: 0;
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
    border-width: 0px;
    box-shadow: 0 1px 0 0 colors.$color-grey-200;
    z-index: 20;
  }

  &.selection-list--first-element-sticky-grouped {
    &::before {
      height: 74px;
    }
  }
}

.selection-list--last-element-sticky {
  &::after {
    content: '';
    background: colors.$color-grey-50;
    position: absolute;
    width: 100%;
    height: 43px;
    left: 0;
    right: 0;
    bottom: 0;
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
    border-width: 0px;
    box-shadow: 0 -1px 0 0 colors.$color-grey-200;
  }

  &.selection-list--last-element-sticky-grouped {
    &::after {
      height: 74px;
    }
  }
}

[class*='selection-list__row--sticky'] {
  --background: colors.$color-grey-50;

  position: sticky;
  background: colors.$color-grey-50;
  z-index: 100;
}

.selection-list__row--sticky-first {
  top: 0;
  box-shadow:
    // covers the gap between item and top of the list / group
    0 calc(var(--input-gap) * -2) 0 0 colors.$color-grey-50,
    // covers the gap between item and next item
    0 calc(var(--input-gap) * 1.5) 0 0 colors.$color-grey-50;

  + .selection-list__row {
    // extra margin to not overlap the next item's focus outline
    margin-top: var(--sticky-first-extra-gap) !important;
  }
}

.selection-list__row--sticky-last {
  bottom: 0;
  box-shadow:
    // Covers the gap between the item and and the bottom of the list
    0 calc(var(--input-gap)) 0 0 colors.$color-grey-50;
}

[class*='selection-list__group--sticky'] {
  position: sticky;
  background: colors.$color-grey-50;
  z-index: 100;
}

.selection-list__group--sticky-first {
  top: 0;
  /**
    * Covers the gap between group and top of the list
    */
  box-shadow: 0 calc(var(--input-gap) * -1) 0 0 colors.$color-grey-50;

  + .selection-list__row {
    top: 28px;
  }
}

.selection-list__group--sticky-last {
  bottom: 36px;
  box-shadow:
    /**
     * Covers the gap between group and item
     */ 0 var(--input-gap) 0 0 colors.$color-grey-50;
}
</style>
